import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sparkles, LogOut, Users, Shield, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";

export default function Navbar() {
  const { pathname } = useLocation();
  const nav = useNavigate();
  const { user, logout, isSysadmin, canManageTeam } = useAuth();
  const onHome = pathname === "/";

  const handleLogout = () => { logout(); nav("/login"); };

  return (
    <header
      data-testid="app-navbar"
      className="sticky top-0 z-50 w-full border-b border-zinc-200 bg-white/75 backdrop-blur-xl"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} data-testid="brand-link" className="flex items-center gap-2 group">
          <span className="h-7 w-7 grid place-items-center bg-zinc-950 text-white rounded-sm">
            <Sparkles className="h-4 w-4" />
          </span>
          <span className="font-display text-[17px] font-semibold tracking-tight text-zinc-950">
            PortalStudio
          </span>
        </Link>

        {user ? (
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-600 font-medium">
            <Link to="/dashboard" className="hover:text-zinc-950 transition-colors" data-testid="nav-dashboard">Dashboard</Link>
            {canManageTeam ? (
              <Link to="/team" className="hover:text-zinc-950 transition-colors" data-testid="nav-team">Team</Link>
            ) : null}
            {isSysadmin ? (
              <Link to="/sysadmin" className="hover:text-zinc-950 transition-colors" data-testid="nav-sysadmin">Sys Admin</Link>
            ) : null}
          </nav>
        ) : (
          <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-600 font-medium">
            <a href="#components" className="hover:text-zinc-950 transition-colors">Components</a>
            <a href="#how" className="hover:text-zinc-950 transition-colors">How it works</a>
          </nav>
        )}

        <div className="flex items-center gap-2">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-trigger"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-zinc-100"
                >
                  <div className="h-7 w-7 rounded-full bg-zinc-950 text-white grid place-items-center text-xs font-semibold">
                    {(user.name || user.email)[0]?.toUpperCase()}
                  </div>
                  <span className="text-sm text-zinc-700 hidden sm:inline">
                    {user.name || user.email}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col">
                    <span className="text-sm text-zinc-950 font-medium truncate">{user.name || user.email}</span>
                    <span className="text-xs text-zinc-500 truncate">{user.email}</span>
                    <span className="text-[10px] uppercase tracking-wider text-zinc-400 mt-1">
                      {user.role.replace("_", " ")}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav("/dashboard")} data-testid="menu-dashboard">
                  <LayoutGrid className="h-4 w-4 mr-2" /> Dashboard
                </DropdownMenuItem>
                {canManageTeam ? (
                  <DropdownMenuItem onClick={() => nav("/team")} data-testid="menu-team">
                    <Users className="h-4 w-4 mr-2" /> Team
                  </DropdownMenuItem>
                ) : null}
                {isSysadmin ? (
                  <DropdownMenuItem onClick={() => nav("/sysadmin")} data-testid="menu-sysadmin">
                    <Shield className="h-4 w-4 mr-2" /> Sys Admin
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : onHome ? (
            <Link to="/login" data-testid="nav-login-btn">
              <Button className="rounded-md bg-zinc-950 text-white hover:bg-zinc-800 transition-all">
                Sign in
              </Button>
            </Link>
          ) : (
            <Link to="/" data-testid="nav-home-btn">
              <Button variant="ghost" className="rounded-md">Back to home</Button>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
